import axios from "axios";
import config from "../config/config.js";
import { isPreviewMode, previewExchangeProducts } from "../preview/fixtures.js";
import {
  PREVIEW_SEARCH_PAGE_SIZE,
  SEARCH_PAGE_SIZE,
  matchesPreviewSearch,
  normalizeProductPage,
  paginatePreviewProducts,
} from "../utils/productSearch.js";
import { mergeResolutionSettings, omitSecrets } from "./resolutions.js";

const http = axios.create({
  baseURL: config.api,
});

function unwrapData(payload) {
  if (!payload || typeof payload !== "object") {
    return payload;
  }
  if (payload.store) {
    return omitSecrets(payload.store);
  }
  if (payload.resolutionSettings) {
    return payload.resolutionSettings;
  }
  return omitSecrets(payload);
}

export { enabledResolutions, mergeResolutionSettings, omitSecrets } from "./resolutions.js";

export async function fetchStore(shop) {
  const { data } = await http.get(`/stores/${shop}`);
  return unwrapData(data);
}

export async function fetchResolutionSettings(shop) {
  try {
    const { data } = await http.get(`/stores/${shop}/resolution-settings`);
    return mergeResolutionSettings(unwrapData(data));
  } catch (error) {
    if (error?.response?.status === 404) {
      return mergeResolutionSettings();
    }
    throw error;
  }
}

export async function fetchOrder(shop, name, email) {
  const { data } = await http.get(`/theme-extension/${shop}/${encodeURIComponent(name)}/${encodeURIComponent(email)}`);
  return omitSecrets(data?.order || data);
}

export async function interpretAddress(payload) {
  const { data } = await http.post("/interpreter", payload);
  return data;
}

export async function fetchExchangeProducts(shop, { name, email, query, maxPrice, after, first } = {}) {
  const pageSize = first || SEARCH_PAGE_SIZE;
  if (isPreviewMode()) {
    const matches = previewExchangeProducts.filter((product) => matchesPreviewSearch(product, query));
    return paginatePreviewProducts(matches, { after, first: PREVIEW_SEARCH_PAGE_SIZE });
  }
  const params = new URLSearchParams();
  if (query) {
    params.set("q", query);
  }
  if (maxPrice != null && maxPrice !== "") {
    params.set("maxPrice", String(maxPrice));
  }
  if (after) {
    params.set("after", String(after));
  }
  params.set("first", String(pageSize));
  const qs = params.toString();
  const paths = [
    name && email
      ? `/theme-extension/${shop}/${encodeURIComponent(name)}/${encodeURIComponent(email)}/exchange-products?${qs}`
      : null,
    `/stores/${shop}/exchange-products?${qs}`,
  ].filter(Boolean);
  for (const path of paths) {
    try {
      const { data } = await http.get(path);
      const list = data?.products || data?.variants || (Array.isArray(data) ? data : []);
      return normalizeProductPage({
        products: omitSecrets(list),
        pageInfo: data?.pageInfo,
      });
    } catch (error) {
      if (error?.response?.status && error.response.status !== 404) {
        throw error;
      }
    }
  }
  return normalizeProductPage([]);
}

export async function createOccurrence(payload) {
  const { data } = await http.post("/occurrences", payload);
  return data;
}

export async function fetchOccurrenceStatus(shop, name, email) {
  // Rota pública: confere nome + e-mail do pedido e devolve só a solicitação atual.
  try {
    const { data } = await http.get(
      `/theme-extension/${shop}/${encodeURIComponent(name)}/${encodeURIComponent(email)}/status`,
    );
    return omitSecrets(data?.occurrence || null);
  } catch (error) {
    if (error?.response?.status === 404) return null;
    throw error;
  }
}

/** Cliente pede um novo código de postagem (o anterior venceu). */
export async function reissuePostCode(shop, name, email) {
  const { data } = await http.post(
    `/theme-extension/${shop}/${encodeURIComponent(name)}/${encodeURIComponent(email)}/reissue-post-code`,
  );
  return omitSecrets(data?.occurrence || null);
}

/** Envia fotos/vídeo/PDF da solicitação. Devolve [{ fileName, originalName, mimeType, size }]. */
export async function uploadAttachments(files) {
  const body = new FormData();
  Array.from(files || []).forEach((file) => body.append("files", file));
  const { data } = await http.post("/attachments", body);
  return data?.files || [];
}
